import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  ExternalLink,
  Hash,
  LayoutDashboard,
  MessageSquare,
  UserRound,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyButton } from '@/components/copy-button';
import { EmptyState, ErrorState } from '@/components/states';
import { ProcessingStatusBadge, ScreenshotStatusBadge } from '@/components/status-badge';
import { CategoryBadges } from '@/features/links/category-badges';
import { LinkAdminActions } from '@/features/links/link-admin-actions';
import { LinkFavicon, LinkThumbnail } from '@/features/links/link-media';
import { useCategoryLabels } from '@/hooks/use-categories';
import { useLinkProgress, type LinkProgress } from '@/hooks/use-link-progress';
import { useLinkQuery } from '@/hooks/use-links';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-client';
import { useIsAdmin } from '@/lib/current-user';
import { channelLabel, firstSharerName, formatAbsoluteTitle, formatDateTime } from '@/lib/format';
import { IN_FLIGHT_POLL_MS, isEnrichmentInFlight } from '@/lib/link-progress';
import type { LinkDetail } from '@/types/api';

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 last:border-b-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right text-sm">{children}</dd>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-hidden="true">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-2/3" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Skeleton className="aspect-[16/10] w-full" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Tells the viewer when background work they were watching settles: a retry can take a minute,
 * and a badge changing colour is easy to miss. Only transitions seen on this page count, so
 * opening a link that finished long ago says nothing.
 */
function useCompletionToasts(link: LinkDetail | undefined, progress: LinkProgress) {
  const { toast } = useToast();
  const previous = useRef<LinkProgress | undefined>(undefined);

  useEffect(() => {
    const before = previous.current;
    previous.current = progress;
    if (!link || !before) return;

    if (before.enriching && !progress.enriching) {
      if (link.processingStatus === 'FAILED') {
        toast({
          title: 'Enrichment failed',
          ...(link.failureReason ? { description: link.failureReason } : {}),
          variant: 'error',
        });
      } else if (!isEnrichmentInFlight(link)) {
        // (Still in flight means the page stopped following a stuck link — nothing finished.)
        toast({
          title: 'Enrichment finished',
          description: progress.capturing
            ? 'Metadata updated. The screenshot is being captured now.'
            : 'Metadata updated.',
          variant: 'success',
        });
      }
    }

    if (before.capturing && !progress.capturing && !progress.enriching) {
      if (link.screenshotStatus === 'COMPLETE') {
        toast({ title: 'Screenshot ready', variant: 'success' });
      } else if (link.screenshotStatus === 'FAILED' || link.screenshotStatus === 'BLOCKED') {
        toast({
          title: 'Screenshot failed',
          ...(link.failureReason ? { description: link.failureReason } : {}),
          variant: 'error',
        });
      }
    }
  }, [link, progress, toast]);
}

/**
 * SECURITY, DELIBERATE: title, description, site name and every URL variant below come from
 * arbitrary third-party pages. They are rendered as JSX text nodes only. This app never uses
 * `dangerouslySetInnerHTML`.
 */
export function LinkDetailPage() {
  const { linkId = '' } = useParams<{ linkId: string }>();
  const isAdmin = useIsAdmin();
  // The query is read before its progress can be known, so the interval follows one render behind.
  const [pollMs, setPollMs] = useState<number | false>(false);
  const query = useLinkQuery(linkId, { pollMs });
  const progress = useLinkProgress(query.data);
  const categoryLabels = useCategoryLabels();
  useCompletionToasts(query.data, progress);
  useEffect(
    () => setPollMs(progress.watching ? IN_FLIGHT_POLL_MS : false),
    [progress.watching, setPollMs],
  );

  if (query.isPending) return <DetailSkeleton />;

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.isNotFound) {
      return (
        <EmptyState
          title="This link no longer exists"
          description="It may have been permanently deleted by an administrator."
          action={
            <Button asChild variant="outline">
              <Link to="/">Back to the catalogue</Link>
            </Button>
          }
        />
      );
    }
    return (
      <ErrorState
        title="Could not load this link"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const link: LinkDetail = query.data;
  const title = link.title?.trim() || link.domain;
  const sharer = firstSharerName(link.firstShare);
  const fromDashboard = link.firstShare.source === 'dashboard';

  return (
    <article className="flex flex-col gap-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/">
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            Back to catalogue
          </Link>
        </Button>
      </div>

      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <LinkFavicon link={link} />
            {link.siteName?.trim() || link.domain}
          </span>
          <ProcessingStatusBadge status={link.processingStatus} busy={progress.enriching} />
          <ScreenshotStatusBadge
            status={link.screenshotStatus}
            busy={progress.capturing && !progress.enriching}
          />
          {link.archived ? (
            <Badge variant="outline" className="gap-1">
              <Archive aria-hidden="true" className="h-3 w-3" />
              Archived
            </Badge>
          ) : null}
          {link.categoryManualOverride ? (
            <Badge variant="muted" title="An administrator set these categories by hand">
              Categories set manually
            </Badge>
          ) : null}
        </div>

        <h1 className="text-2xl font-semibold leading-tight tracking-tight">{title}</h1>

        {link.description?.trim() ? (
          <p className="max-w-3xl text-sm text-muted-foreground">{link.description}</p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button asChild size="sm">
            <a href={link.originalUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink aria-hidden="true" className="h-4 w-4" />
              Open original page
            </a>
          </Button>
          {/* The URLs card is gone, but copying the link was the one thing it was good for. */}
          <CopyButton value={link.originalUrl} label="link URL" />
          {link.firstShare.permalink ? (
            <Button asChild size="sm" variant="outline">
              <a href={link.firstShare.permalink} target="_blank" rel="noopener noreferrer">
                <MessageSquare aria-hidden="true" className="h-4 w-4" />
                View first Slack message
              </a>
            </Button>
          ) : null}
        </div>

        <CategoryBadges slugs={link.categories} labels={categoryLabels} max={12} />
      </header>

      {isAdmin ? <LinkAdminActions link={link} progress={progress} /> : null}

      {link.processingStatus === 'FAILED' && link.failureReason ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4"
        >
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              Processing failed{link.failureStage ? ` during ${link.failureStage}` : ''}
            </p>
            <p className="mt-1 break-words text-sm text-muted-foreground">{link.failureReason}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {link.attempts} attempt{link.attempts === 1 ? '' : 's'} so far.
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-6">
          <figure className="flex flex-col gap-2">
            <a
              href={link.originalUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${title} in a new tab`}
              className="block overflow-hidden rounded-lg border border-border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <div className="aspect-[16/10] w-full">
                <LinkThumbnail
                  link={{
                    thumbnailUrl: link.screenshotUrl ?? link.thumbnailUrl,
                    ogImageUrl: link.ogImageUrl,
                    screenshotStatus: link.screenshotStatus,
                    title: link.title,
                    domain: link.domain,
                  }}
                  imageClassName="object-top"
                />
              </div>
            </a>
            <figcaption className="text-xs text-muted-foreground">
              {link.screenshotCapturedAt
                ? `Screenshot captured ${formatDateTime(link.screenshotCapturedAt)}. Click to open the original page.`
                : 'Click to open the original page.'}
            </figcaption>
          </figure>

          {/*
            Only rendered when an LLM enrichment provider actually wrote one. Under the
            `metadata` provider there is nothing to show, and an empty card would read as a
            failure rather than as a feature that is switched off.
          */}
          {link.tldr?.trim() ? (
            <Card>
              <CardHeader>
                <CardTitle>What this site is about</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">{link.tldr}</p>
              </CardContent>
            </Card>
          ) : null}
        </div>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              {/* Wording is load-bearing: SLC only ever stores the first share. */}
              <CardTitle>First share</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex items-start gap-2">
                <UserRound
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {fromDashboard ? 'Added by' : 'First shared by'}
                  </p>
                  <p className="truncate text-sm font-medium">{sharer}</p>
                  {link.firstShare.slackUserEmail ? (
                    <p className="truncate text-xs text-muted-foreground">
                      {link.firstShare.slackUserEmail}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex items-start gap-2">
                {fromDashboard ? (
                  <LayoutDashboard
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  />
                ) : (
                  <Hash
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  />
                )}
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {fromDashboard ? 'Added on' : 'First seen in'}
                  </p>
                  <p className="truncate text-sm font-medium">{channelLabel(link.firstShare)}</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <MessageSquare
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {fromDashboard ? 'Added at' : 'First shared at'}
                  </p>
                  <p className="text-sm font-medium">
                    <time
                      dateTime={link.firstShare.sharedAt}
                      title={formatAbsoluteTitle(link.firstShare.sharedAt)}
                    >
                      {formatDateTime(link.firstShare.sharedAt)}
                    </time>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <MetaRow label="Domain">{link.domain}</MetaRow>
                {link.siteName ? <MetaRow label="Site name">{link.siteName}</MetaRow> : null}
                {link.language ? <MetaRow label="Language">{link.language}</MetaRow> : null}
                {link.contentType ? (
                  <MetaRow label="Content type">{link.contentType}</MetaRow>
                ) : null}
                <MetaRow label="Category confidence">
                  {Math.round(link.categoryConfidence * 100)}%
                </MetaRow>
                {link.autoCategories.length > 0 ? (
                  <MetaRow label="Auto categories">{link.autoCategories.join(', ')}</MetaRow>
                ) : null}
                <MetaRow label="Added to catalogue">
                  <time dateTime={link.createdAt} title={formatAbsoluteTitle(link.createdAt)}>
                    {formatDateTime(link.createdAt)}
                  </time>
                </MetaRow>
                <MetaRow label="Last updated">
                  <time dateTime={link.updatedAt} title={formatAbsoluteTitle(link.updatedAt)}>
                    {formatDateTime(link.updatedAt)}
                  </time>
                </MetaRow>
                <MetaRow label="Link id">
                  <span className="font-mono text-xs">{link.linkId}</span>
                </MetaRow>
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
    </article>
  );
}
